"use client";

import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";

gsap.registerPlugin(SplitText, ScrambleTextPlugin);

export interface ScrambledTextProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  /** How close the pointer has to be, in px, before a character scrambles. */
  radius?: number;
  duration?: number;
  speed?: number;
  scrambleChars?: string;
  children: ReactNode;
}

/**
 * Text whose characters scramble as the pointer passes over them.
 *
 * From React Bits (reactbits.dev), after Tom Miller's GSAP pen, with three changes:
 *
 * 1. **No baked-in styling.** Upstream hardcodes `m-[7vw] max-w-[800px] font-mono
 *    text-white`, which would drop a 7vw margin and white type into a paragraph that
 *    already has both. Type and spacing come from the call site.
 * 2. **Characters are width-locked before scrambling.** Upstream gets away with a
 *    proportional font because it sets one; substituting a narrow "." for a wide "m" in
 *    Space Grotesk reflows the whole line, and the paragraph jitters as the pointer
 *    crosses it. Each character is pinned to its natural width first, so the scramble
 *    changes glyphs without moving anything.
 * 3. **Reduced motion is respected.** The text renders plainly and no listener is bound.
 *
 * Nested markup survives: SplitText keeps ancestor elements, so an accented word inside
 * the sentence keeps its colour while its characters scramble.
 */
export const ScrambledText = ({
  radius = 100,
  duration = 1.2,
  speed = 0.5,
  scrambleChars = ".:",
  children,
  ...rest
}: ScrambledTextProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = rootRef.current;
    const target = el?.querySelector("p");
    if (!el || !target || reduce) return;

    // Words as well as characters: with characters alone every glyph is its own
    // inline-block, the line may break between any two of them, and words split
    // mid-way across lines ("tech a / nd"). A word box keeps each word whole.
    const split = SplitText.create(target, {
      type: "words,chars",
      // The paragraph is visual only (see the render below), so SplitText adds no labels:
      // its default puts aria-label on the <p>, which a paragraph is not allowed to carry.
      aria: "none",
      wordsClass: "inline-block whitespace-nowrap",
      charsClass: "inline-block will-change-transform",
    });

    split.chars.forEach((node) => {
      const char = node as HTMLElement;
      gsap.set(char, { attr: { "data-content": char.innerHTML } });
      // Pin the width before any glyph is swapped, or the line reflows mid-scramble.
      const { width } = char.getBoundingClientRect();
      if (width) char.style.width = `${width}px`;
      char.style.textAlign = "center";
    });

    const handleMove = (event: PointerEvent) => {
      split.chars.forEach((node) => {
        const char = node as HTMLElement;
        const { left, top, width, height } = char.getBoundingClientRect();
        const dist = Math.hypot(event.clientX - (left + width / 2), event.clientY - (top + height / 2));
        if (dist >= radius) return;

        gsap.to(char, {
          overwrite: true,
          duration: duration * (1 - dist / radius),
          scrambleText: { text: char.dataset.content ?? "", chars: scrambleChars, speed },
          ease: "none",
        });
      });
    };

    el.addEventListener("pointermove", handleMove);
    return () => {
      el.removeEventListener("pointermove", handleMove);
      split.revert();
    };
  }, [radius, duration, speed, scrambleChars, reduce]);

  return (
    // Extra props land on the wrapper, so a caller can hang the page's own animation
    // hooks (data-copy in the hero) on it without another element in between.
    <div ref={rootRef} {...rest}>
      {/* Split into letter boxes for the effect, so assistive tech reads the sentence from
          the plain copy beside it instead of letter by letter. */}
      <p aria-hidden>{children}</p>
      <span className="sr-only">{children}</span>
    </div>
  );
};
